import { Grid } from '@mui/material';
import { dateFormat, pastDateTime } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, RoutalTooltip, VSpacer } from '@nx-smartmonkey/ui';
import { useState } from 'react';
import styled from 'styled-components';

import tinycolor from 'tinycolor2';
import More3Points from '../../../assets/svg/More3Points';
import { HeaderContent } from '../../../components/HeaderContent';
import { GeoFence } from '../../../domain/geoFence/GeoFence';
import { Project } from '../../../domain/project/Project';
import { Vehicle } from '../../../domain/vehicle/Vehicle';
import { useUpdateProject } from '../../../hooks/project/useUpdateProject';
import { useRetrieveRightPanelInfo } from '../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveSupportDocs } from '../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../hooks/user/useRetrieveUserCredentials';
import Button from '../../../react-components/Button';
import Text from '../../../react-components/Text';
import { DataTable } from '../../../react-components/data-table/DataTable';
import { PlannerGeoFencesActionsMenu } from './PlannerGeoFencesActionsMenu';

type PlannerGeoFencesListContextAnchor = {
  screenPosition: { top: number; left: number };
  geoFence: GeoFence;
};

const ListContainerBody = styled.div`
  height: calc(100vh - 124px);
  display: flex;
  flex-direction: column;
`;

type PlannerGeoFencesListProps = {
  project: Project;
  geoFences: GeoFence[];
  selectedGeoFenceIds: string[];
  projectVehicles: Vehicle[];
  isDrawing: boolean;
  setEditingGeoFence?: (geoFence: GeoFence) => void;
  setSelectedGeoFenceIds: (selection: string[]) => void;
  setHighlightedGeoFenceId: (highlightedGeoFenceId: string | undefined) => void;
};

export const PlannerGeoFencesList = ({
  project,
  geoFences,
  selectedGeoFenceIds,
  projectVehicles,
  isDrawing,
  setEditingGeoFence,
  setSelectedGeoFenceIds,
  setHighlightedGeoFenceId,
}: PlannerGeoFencesListProps) => {
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { data: user } = useRetrieveUser();
  const { setRightPanelInfo } = useRetrieveRightPanelInfo();
  const [tableContextualAnchor, setTableContextualAnchor] = useState<PlannerGeoFencesListContextAnchor | undefined>(
    undefined
  );
  const { run: updateProject, isLoading: isLoadingProject } = useUpdateProject({ project });

  if (!project) return null;

  return (
    <Grid
      container
      direction="column"
      style={{
        height: `calc(100vh - 8px )`, // -4 -4 due to floating menu margins
        width: `560px`,
        borderRight: `1px solid ${RoutalPalette.neutral10}`,
      }}
    >
      <Grid
        container
        direction="row"
        wrap="nowrap"
        style={{
          padding: `11px 20px`,
          backgroundColor: RoutalPalette.neutral02n,
          borderBottom: `1px solid ${RoutalPalette.neutral10}`,
        }}
      >
        <HeaderContent
          titleIntlId="planner.zones.title"
          article={{
            intlId: `planner.zones.article`,
            link: retrieveDoc({ supportPage: `geoFences`, language: user?.language.value }),
          }}
          video={{
            intlId: `watch_video`,
            link: `https://www.youtube.com/watch?v=pSZ9X5nBzQMs`,
          }}
          styles={{
            container: {
              marginTop: `10px`,
            },
          }}
        />
      </Grid>

      <ListContainerBody style={{ padding: `16px 22px` }}>
        {!project.geo_fences_enabled && (
          <>
            <VSpacer small />

            <Grid
              container
              direction="column"
              alignItems="flex-start"
              wrap="nowrap"
              style={{ backgroundColor: `#FDDB7F`, borderRadius: `4px`, padding: `12px 12px` }}
            >
              <Grid item>
                <Text style={{ fontSize: `12px`, textAlign: `justify` }} intlId="planner.zones.geo_fences_disabled" />
              </Grid>

              <Grid item>
                <Button
                  intlId="planner.zones.geo_fences_disabled_button"
                  onClick={() => {
                    updateProject({ geo_fences_enabled: !project.geo_fences_enabled });
                  }}
                  disabled={!userCredentials?.projects.test(project.id, `can_update_project`) || isLoadingProject}
                />
              </Grid>
            </Grid>
          </>
        )}

        <VSpacer small />

        <VSpacer medium />

        {geoFences.length > 0 && (
          <div style={{ position: `relative`, height: `100%` }}>
            {!isDrawing && selectedGeoFenceIds.length > 0 ? (
              <div style={{ position: `absolute`, right: 0, zIndex: 3 }}>
                <PlannerGeoFencesActionsMenu
                  type="popover"
                  geoFences={geoFences.filter((geoFence) => selectedGeoFenceIds.includes(geoFence.id))}
                  popoverProps={{
                    hideArrow: false,
                    style: {
                      justifyContent: `space-between`,
                      // padding: `4px 12px`,
                    },
                    containerStyle: {
                      width: `240px`,
                    },
                    content: (
                      <Text
                        variant="body"
                        intlId="plans.plan.data.services.header.actions"
                        style={{ cursor: `pointer`, color: RoutalPalette.primary40 }}
                      />
                    ),
                  }}
                  onDeleteSuccess={() => {
                    setSelectedGeoFenceIds([]);
                  }}
                  onEditGeoFence={(geoFence: GeoFence) => {
                    if (setEditingGeoFence) {
                      setEditingGeoFence(geoFence);
                    }
                  }}
                />
              </div>
            ) : null}
            <DataTable
              noFooter
              total={geoFences.length}
              docs={geoFences
                .sort(
                  (a: GeoFence, b: GeoFence) =>
                    new Date(b.created_at ?? ``).getTime() - new Date(a.created_at ?? ``).getTime()
                )
                .map((geoFence: GeoFence) => {
                  const tColor = tinycolor(geoFence.color.value);
                  const geoFenceColor = geoFence.enabled ? geoFence.color.value : tColor.lighten(35).toString();
                  return {
                    doc: geoFence,
                    checkboxIcon: {
                      element: (
                        <div
                          style={{
                            padding: `8px`,
                            margin: `0 2px`,
                            backgroundColor: `${geoFenceColor}`,
                            color: `white`,
                            borderRadius: `2px`,
                            width: `24px`,
                            textAlign: `center`,
                            fontSize: `12px`,
                            lineHeight: 0.5,
                          }}
                        >
                          {geoFence.getShortLabel()}
                        </div>
                      ),
                    },
                  };
                })}
              globalCheckboxColumnStyles={{
                padding: `8px 18px`,
              }}
              onRowMouseEnter={(doc: any) => {
                if (!isDrawing) {
                  // Adds the geo fence to the selected list
                  setHighlightedGeoFenceId(doc.id);
                }
              }}
              onRowMouseLeave={(doc: any) => {
                if (!isDrawing) {
                  // Removes the geo fence from the selected list
                  setHighlightedGeoFenceId(undefined);
                }
              }}
              emptyMessage={<Text intlId="planner.zones.no_geo_fences" />}
              hover
              handleOnContextMenu={(ev: any, geoFence: GeoFence) => {
                ev.preventDefault();
                if (!isDrawing) {
                  setTableContextualAnchor({ screenPosition: { top: ev.clientY, left: ev.clientX }, geoFence });
                }
              }}
              onRowDoubleClick={(geoFence: GeoFence) => {
                if (!isDrawing) {
                  setRightPanelInfo({
                    detailType: `geoFence`,
                    detailId: `${geoFence.id}`,
                    detailTab: null,
                  });
                }
              }}
              selectedIds={selectedGeoFenceIds}
              onMultipleSelection={({ selectedIds, fromGlobalCheckbox }) => {
                if (!isDrawing) {
                  if (fromGlobalCheckbox) {
                    if (selectedGeoFenceIds.length > 0) {
                      setSelectedGeoFenceIds([]);
                    } else {
                      setSelectedGeoFenceIds(geoFences.map((g) => g.id));
                    }
                  } else {
                    setSelectedGeoFenceIds(selectedIds ?? []);
                  }
                }
              }}
              columns={[
                {
                  height: 34,
                  width: 272,
                  accessorName: `label`,
                  name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="planner.zones.name" />,
                  formatter: ({ doc: geoFence }) => {
                    const { created_at, label } = geoFence;
                    return (
                      <Grid container direction="column">
                        <Grid item>
                          {label && label !== `` ? (
                            <RoutalTooltip title={label}>
                              <Text variant={geoFence.enabled ? `body` : `label`}>
                                {label.length > 30 ? `${label.substring(0, 30)}...` : `${label}`}
                              </Text>
                            </RoutalTooltip>
                          ) : (
                            <Text
                              variant={geoFence.enabled ? `body` : `label`}
                              style={{
                                ...(!geoFence.enabled ? { color: RoutalPalette.secondary00 } : {}),
                              }}
                              intlId="no_name"
                            />
                          )}
                        </Grid>

                        <Grid item container direction="row">
                          <Grid item>
                            <Text
                              variant="label"
                              intlId="planner.zones.assigned_vehicles"
                              intlValues={{
                                num: projectVehicles.filter((vehicle) =>
                                  vehicle.default_geo_fences?.includes(geoFence.id)
                                ).length,
                              }}
                            />
                          </Grid>

                          <Grid item>
                            <RoutalTooltip title={dateFormat(created_at)}>
                              <Text variant="label">{`, ${pastDateTime(created_at)}`}</Text>
                            </RoutalTooltip>
                          </Grid>
                        </Grid>
                      </Grid>
                    );
                  },
                },
                {
                  width: 24,
                  accessorName: `id`,
                  align: `right`,
                  columnStyle: {
                    width: `unset`,
                  },
                  formatter: ({ value: id, doc: geo, index, rowHover }) => (
                    <Grid container justifyContent="flex-end" alignItems="center">
                      {rowHover && !isDrawing && (
                        <PlannerGeoFencesActionsMenu
                          geoFences={[geo]}
                          type="popover"
                          popoverProps={{
                            hideArrow: true,
                            content: <More3Points />,
                            style: { color: RoutalPalette.secondary60, padding: 0, margin: 0, border: `unset` },
                          }}
                          onEditGeoFence={(geoFence: GeoFence) => {
                            if (setEditingGeoFence) {
                              setEditingGeoFence(geoFence);
                            }
                          }}
                        />
                      )}
                    </Grid>
                  ),
                },
              ]}
            />

            {tableContextualAnchor && !isDrawing ? (
              <PlannerGeoFencesActionsMenu
                type="contextual"
                contextAnchor={tableContextualAnchor}
                setContextAnchor={() => {
                  setTableContextualAnchor(undefined);
                }}
                geoFences={[tableContextualAnchor.geoFence]}
                onEditGeoFence={(geoFence: GeoFence) => {
                  if (setEditingGeoFence) {
                    setEditingGeoFence(geoFence);
                  }
                }}
              />
            ) : null}
          </div>
        )}
      </ListContainerBody>
    </Grid>
  );
};
